import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core'
import { InvitationTemplatesStep } from '@app/v2/event/enums/invitation-templates-step.enum'
import { InvitationEmailTemplate, InvitationEmailTemplateInfo, InvitationEmailTemplateTiming } from '@app/v2/event/types/invitation-email-template.type'
import { InvitationTemplateChannel } from '@app/v2/event/types/invitation-tracker.type'
import { WhatsAppInviteTemplateModel } from '@app/v2/event/types/whatsapp-invite-template.model'
import { WhatsAppInviteTemplateId } from '@app/v2/event/types/whatsapp-invite-template.type'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { CustomTemplateComponent } from '../custom-template/custom-template.component'
import { WhatsAppInviteTemplatesComponent } from '../whatsapp-invite-templates/whatsapp-invite-templates.component'

export type InvitationTemplatesModalMode = 'save' | 'send-flow'

type ChannelCard = {
  id: InvitationTemplateChannel
  label: string
  selectable: boolean
}

const CHANNEL_CARDS: ChannelCard[] = [
  { id: 'email', label: 'Email', selectable: true },
  { id: 'whatsapp', label: 'WhatsApp', selectable: true }
]

@Component({
  selector: 'app-invitation-templates-modal',
  standalone: true,
  imports: [ModalComponent, NgClass, CustomTemplateComponent, WhatsAppInviteTemplatesComponent],
  templateUrl: './invitation-templates-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvitationTemplatesModalComponent {
  public readonly id = input.required<string>()
  public readonly event = input.required<InvitationEmailTemplate>()
  public readonly eventTimings = input<InvitationEmailTemplateTiming[]>([])
  public readonly selectedPersonaSlug = input<string>('')
  public readonly invitationInfo = input.required<InvitationEmailTemplateInfo>()
  public readonly isLoading = input(false)
  public readonly isSaving = input(false)
  public readonly isSendingTest = input(false)
  public readonly testReceiverEmail = input('')
  public readonly testReceiverPhone = input('')
  public readonly mode = input<InvitationTemplatesModalMode>('save')
  public readonly savedWhatsAppTemplateId = input<WhatsAppInviteTemplateId>(WhatsAppInviteTemplateModel.DEFAULT_TEMPLATE_ID)
  public readonly sendFlowChannel = input<InvitationTemplateChannel>('email')
  public readonly eventId = input<number | null>(null)
  public readonly organizationId = input<number | null>(null)

  public readonly saveRequested = output<InvitationEmailTemplateInfo>()
  public readonly whatsappSaveRequested = output<WhatsAppInviteTemplateId>()
  public readonly sendTestRequested = output<InvitationEmailTemplateInfo>()
  public readonly whatsappSendTestRequested = output<WhatsAppInviteTemplateId>()
  public readonly closed = output<void>()

  public readonly channels = CHANNEL_CARDS
  public readonly InvitationTemplatesStep = InvitationTemplatesStep
  public readonly selectedChannelId = signal<InvitationTemplateChannel | null>(null)
  public readonly step = signal<InvitationTemplatesStep>(InvitationTemplatesStep.CHANNEL)
  public readonly whatsappSelectedTemplateId = signal<WhatsAppInviteTemplateId>(WhatsAppInviteTemplateModel.DEFAULT_TEMPLATE_ID)

  public readonly isTemplateStep = computed(() => this.step() === InvitationTemplatesStep.EMAIL || this.step() === InvitationTemplatesStep.WHATSAPP)
  public readonly header = computed(() => {
    if (this.step() === InvitationTemplatesStep.EMAIL) return 'Email template'
    if (this.step() === InvitationTemplatesStep.WHATSAPP) return 'WhatsApp template'
    return 'Select invitation channel'
  })
  public readonly widthClass = computed(() => (this.isTemplateStep() ? 'tw-w-[343px] lg:tw-w-[800px]' : 'tw-w-[343px] lg:tw-w-[600px]'))
  public readonly heightClass = computed(() => (this.isTemplateStep() ? 'tw-h-[90vh] lg:tw-h-[864px] tw-max-h-[90vh]' : 'tw-h-auto'))
  public readonly isSendFlow = computed(() => this.mode() === 'send-flow')
  public readonly primaryCtaLabel = computed(() => {
    if (this.isSaving()) return 'Saving…'
    return this.isSendFlow() ? 'Save and send' : 'Save as default'
  })
  public readonly testReceiver = computed(() => (this.step() === InvitationTemplatesStep.WHATSAPP ? this.testReceiverPhone() : this.testReceiverEmail()).trim())
  public readonly canSendTest = computed(() => !!this.testReceiver())
  public readonly sendTestCtaLabel = computed(() => {
    if (this.isSendingTest()) return 'Sending test…'
    const receiver = this.testReceiver()
    return receiver ? `Send a test to (${receiver})` : 'Send a test'
  })
  public readonly isSavingOrSendingTest = computed(() => this.isSaving() || this.isSendingTest())

  private readonly _customTemplate = viewChild(CustomTemplateComponent)
  private readonly _alertService = inject(AlertService)

  constructor() {
    effect(
      () => {
        if (this.mode() !== 'send-flow') return
        const channel = this.sendFlowChannel()
        this.selectedChannelId.set(channel)
        if (channel === 'whatsapp') {
          this.whatsappSelectedTemplateId.set(untracked(this.savedWhatsAppTemplateId))
          this.step.set(InvitationTemplatesStep.WHATSAPP)
          return
        }
        this.step.set(InvitationTemplatesStep.EMAIL)
      },
      { allowSignalWrites: true }
    )
  }

  /**
   * Handles channel card click. Email / WhatsApp advance to their template step in the same modal.
   * @param channel - Selected channel card.
   */
  public onChannelClick(channel: ChannelCard): void {
    if (!channel.selectable) return
    this.selectedChannelId.set(channel.id)
    if (channel.id === 'email') {
      this.step.set(InvitationTemplatesStep.EMAIL)
      return
    }
    if (channel.id === 'whatsapp') {
      this.whatsappSelectedTemplateId.set(this.savedWhatsAppTemplateId())
      this.step.set(InvitationTemplatesStep.WHATSAPP)
    }
  }

  /**
   * Returns from the template step to channel selection (same modal).
   */
  public onBack(): void {
    this.step.set(InvitationTemplatesStep.CHANNEL)
    this.selectedChannelId.set(null)
  }

  /**
   * Emits the selected WhatsApp template id, or flushes email editor state and emits save.
   */
  public onPrimaryCta(): void {
    if (this.isSavingOrSendingTest()) return
    if (this.step() === InvitationTemplatesStep.WHATSAPP) {
      this.whatsappSaveRequested.emit(this.whatsappSelectedTemplateId())
      return
    }
    if (!this._prepareEmailTemplateForSubmit()) return
    this.saveRequested.emit(this.invitationInfo())
  }

  /**
   * Emits WhatsApp send-test with the selected template id, or flushes email editor and emits email send-test.
   */
  public onSendTestCta(): void {
    if (this.isSavingOrSendingTest() || !this.canSendTest()) return
    if (this.step() === InvitationTemplatesStep.WHATSAPP) {
      this.whatsappSendTestRequested.emit(this.whatsappSelectedTemplateId())
      return
    }
    if (!this._prepareEmailTemplateForSubmit()) return
    this.sendTestRequested.emit(this.invitationInfo())
  }

  /**
   * Resets internal step when the modal is dismissed via X / backdrop.
   */
  public onModalClose(): void {
    this.step.set(InvitationTemplatesStep.CHANNEL)
    this.selectedChannelId.set(null)
    this.whatsappSelectedTemplateId.set(WhatsAppInviteTemplateModel.DEFAULT_TEMPLATE_ID)
    this.closed.emit()
  }

  /**
   * Validates the email subject (showing its error) and flushes editor state into `invitationInfo`.
   * @returns False when the subject is invalid and the save / send-test should not be emitted.
   */
  private _prepareEmailTemplateForSubmit(): boolean {
    const customTemplate = this._customTemplate()
    if (!customTemplate) return true
    if (!customTemplate.validateSubject()) return false
    customTemplate.flushEditorToInvitationInfo()
    return true
  }

  /**
   * Blocks save/send while footer is busy or the banner is still uploading / being cropped.
   */
  private _canEmitTemplateAction(): boolean {
    if (this.isSavingOrSendingTest()) return false
    if (this._customTemplate()?.isBannerBusy()) {
      this._alertService.alert(Alert.WARNING, 'Please wait for the image to finish uploading.')
      return false
    }
    return true
  }
}
